import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { customerAuditLogQueryOptions } from '../../lib/queries/customers';
import type { AuditEntry } from '../../lib/queries/customers';

export default function AuditLog({ id }: { id: string }) {
  const [auditPage, setAuditPage] = useState(1);

  const { data: auditLog, isLoading: auditLoading } = useQuery(
    customerAuditLogQueryOptions(id, auditPage),
  );

  return (
    <div className="space-y-4">
      <p className="text-subtle text-xs">
        Append-only record of all privileged actions on this tenant.
      </p>

      <div className="bg-bg-elevated divide-y divide-white/6 rounded-lg border border-white/6">
        {auditLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="m-1 h-14 animate-pulse rounded bg-white/3"
            />
          ))
        ) : !auditLog || auditLog.entries.length === 0 ? (
          <p className="text-muted p-6 text-sm">No audit log entries.</p>
        ) : (
          auditLog.entries.map((entry: AuditEntry) => {
            const ts = new Date(entry.createdAt);
            const label = `${ts.toLocaleString('en-US', { month: 'short' })} ${ts.getDate()} ${String(ts.getHours()).padStart(2, '0')}:${String(ts.getMinutes()).padStart(2, '0')}`;
            return (
              <div key={entry.id} className="flex items-start gap-4 px-4 py-3">
                <span className="text-subtle w-28 shrink-0 pt-0.5 text-xs tabular-nums">
                  {label}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-primary text-xs">
                    <span className="text-accent font-medium">
                      {entry.actorName}
                    </span>{' '}
                    {entry.action}
                  </p>
                  {entry.tag && (
                    <span className="text-subtle mt-1 inline-block rounded border border-white/10 px-2 py-0.5 text-xs">
                      {entry.tag}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {auditLog && auditLog.totalPages > 1 && (
        <div className="flex items-center justify-end gap-2">
          <button
            disabled={auditPage <= 1}
            onClick={() => setAuditPage((p) => p - 1)}
            className="text-muted hover:text-primary text-sm disabled:opacity-30"
          >
            ← Prev
          </button>
          <span className="text-muted text-xs">
            {auditPage} / {auditLog.totalPages}
          </span>
          <button
            disabled={auditPage >= auditLog.totalPages}
            onClick={() => setAuditPage((p) => p + 1)}
            className="text-muted hover:text-primary text-sm disabled:opacity-30"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
